import {
  Divider,
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownTrigger,
  User,
} from "@heroui/react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faKey,
  faSignOutAlt,
  faUser,
  faFileContract,  
} from "@fortawesome/free-solid-svg-icons";
import { FC } from "react";
import { useNavigate } from "react-router-dom";
import axiosInstance from "../../api/axiosInstance";
import { TLoggedInUserInfo } from "../../types/loggedInUserInfo";
import { useSubscriptions } from "../../../features/auth/hooks/useSubscriptions";
import { deleteCookie } from "../../utils/helpers";
import ModalManager from "../modal/Modal-Manager";
import { trackException, trackInfo, LogLevel } from "../../services/appInsights";
import { AppInsightsActions, AppInsightsComponents, AppInsightsErrorTypes } from "../../services/appInsightsConstants";

interface IUserProfileMenuProps {
  loggedInUserInfo: TLoggedInUserInfo;
}

const UserProfileMenu: FC<IUserProfileMenuProps> = ({ loggedInUserInfo }) => {
  const horizonAppUrl = window.config.HORIZON_APP_URL;
  const navigate = useNavigate();
  const { isSubscribedToHorizon } = useSubscriptions(loggedInUserInfo);

  const onSignOut = async () => {
    try {
      // Track logout attempt
      trackInfo('User initiated logout from profile menu', {
        component: AppInsightsComponents.USER_PROFILE_MENU,
        action: AppInsightsActions.LOGOUT_ATTEMPT,
        userId: loggedInUserInfo.id,
        emailAddress: loggedInUserInfo.emailAddress
      });

      await axiosInstance.post("/token/logout", {}, { withCredentials: true });
      
      // Track successful logout
      trackInfo('Logout successful from profile menu', {
        component: AppInsightsComponents.USER_PROFILE_MENU,
        action: AppInsightsActions.LOGOUT_SUCCESS,
        userId: loggedInUserInfo.id,
        emailAddress: loggedInUserInfo.emailAddress
      });

      deleteCookie("MSILoggedInEmailAddress");
      navigate("/account/login", { replace: true });
    } catch (error) {
      // Track logout failure
      trackException(
        error as Error,
        LogLevel.Error,
        {
          component: AppInsightsComponents.USER_PROFILE_MENU,
          action: AppInsightsActions.LOGOUT_FAILURE,
          errorType: AppInsightsErrorTypes.API_ERROR,
          userId: loggedInUserInfo.id,
          emailAddress: loggedInUserInfo.emailAddress,
          errorMessage: (error as Error).message
        }
      );

      console.error("Logout failed:", error);
      deleteCookie("MSILoggedInEmailAddress");
      navigate("/error", { replace: true });
      // Optionally handle error, e.g. show a message
    }
  };

  // const disabledKeys = ['my-profile', 'change-password', 'subscriptions'];

  return (
    <div className="text-msi-black-color">
      <Dropdown placement="bottom-end" radius="none">
        <DropdownTrigger>
          <User
            as="button"
            className="transition-transform align-middle"
            name={loggedInUserInfo.fullName}
            description={loggedInUserInfo.emailAddress}
            avatarProps={{
              icon: (
                <div className="avatar-circle">
                  <span className="initials">
                    {loggedInUserInfo.fullName
                      .replace(/[^a-zA-Z- ]/g, "")
                      .match(/\b\w/g) || ""}
                  </span>
                </div>
              ),
              size: "lg",
            }}
            classNames={{
              name: "text-md",
            }}
          />
        </DropdownTrigger>
        <DropdownMenu aria-label="Profile Actions" variant="flat">
          <DropdownItem
            key="profile"
            className="h-8 gap-2 hover:rounded-none"
            isReadOnly={true}
          >
            <p className="font-semibold">{loggedInUserInfo.clientName}</p>
          </DropdownItem>
          <DropdownItem key="divider">
            <Divider />
          </DropdownItem>
          <DropdownItem
            key="my-profile"
            className="hover:rounded-none"
            onPress={() => {
              // Track profile menu selection
              trackInfo('User accessed profile menu', {
                component: AppInsightsComponents.USER_PROFILE_MENU,
                action: AppInsightsActions.NAVIGATION_SUCCESS,
                targetPage: 'my-profile',
                userId: loggedInUserInfo.id,
                isSubscribedToHorizon: isSubscribedToHorizon
              });

              if (!isSubscribedToHorizon) {
                ModalManager.show({
                  header: "Contact MSI for Account Assistance",
                  message: "Please contact MSI to update your profile.",
                  showActionButton: false,
                  cancelButtonText: "Close",
                  size: "sm",
                });
                return;
              }
              window.location.href = `${horizonAppUrl}/user/my-profile`;
            }}
          >
            <FontAwesomeIcon icon={faUser} className="icon mr-2" />
            My Profile
          </DropdownItem>
          <DropdownItem
            key="change-password"
            className="hover:rounded-none"
            onPress={() => {
              // Track change password navigation
              trackInfo('User navigated to change password', {
                component: AppInsightsComponents.USER_PROFILE_MENU,
                action: AppInsightsActions.NAVIGATION_SUCCESS,
                targetPage: 'change-password',
                userId: loggedInUserInfo.id
              });

              navigate("/account/change-password");
            }}
          >
            <FontAwesomeIcon icon={faKey} className="icon mr-2" />
            Change Password
          </DropdownItem>
          <DropdownItem
            key="subscriptions"
            className="hover:rounded-none"
            onPress={() => {
              // Track subscriptions menu selection
              trackInfo('User accessed subscriptions menu', {
                component: AppInsightsComponents.USER_PROFILE_MENU,
                action: AppInsightsActions.NAVIGATION_SUCCESS,
                targetPage: 'subscriptions',
                userId: loggedInUserInfo.id,
                isSubscribedToHorizon: isSubscribedToHorizon
              });

              if (!isSubscribedToHorizon) {
                ModalManager.show({
                  header: "Contact MSI for Account Assistance",
                  message:
                    "Please contact MSI to get information about your subscriptions and products.",
                  showActionButton: false,
                  cancelButtonText: "Close",
                  size: "sm",
                });
                return;
              }
              window.location.href = `${horizonAppUrl}/user/subscriptions-and-data-updates`;
            }}
          >
            <FontAwesomeIcon icon={faFileContract} className="icon mr-2" />
            Subscriptions & Data Updates
          </DropdownItem>
          <DropdownItem
            key="logout"
            color="danger"
            className="hover:rounded-none"
            onPress={onSignOut}
          >
            <FontAwesomeIcon icon={faSignOutAlt} className="icon mr-2" />
            Logout
          </DropdownItem>
        </DropdownMenu>
      </Dropdown>
    </div>
  );
};

export default UserProfileMenu;
